import React, { useState, useEffect, useMemo, useCallback } from "react";

import {
  Zap,
  Users,
  Pen,
  Clock,
  Building2,
  MapPin,
  Edit2,
  Info,
  Check,
  CircleFadingArrowUp,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import Accordion from "../../../Components/Accordion";
import Button from "../../../Components/Button";
import Modal from "../../../Components/Modal";
import RadioButton from "../../../Components/RadioButton";
import SearchInput from "../../../Components/Search/SearchInput";
import SuccessNotification from "../../../Components/SuccessNotification";
import TabsComponent from "../../../Components/Tab";
import { useAppDispatch, useAppSelector } from "../../../Store/Hooks";
import {
  fetchRecommendations as fetchRecommendationsAction,
  clearRecommendations,
  submitAssignment,
  clearSubmitAssignment,
  fetchUnassignedCustomers,
  fetchPendingApprovals,
  ExecutiveFromAPI,
} from "../../../Store/Reducers/AssignmentSlice";
import { fetchAdminUsers } from "../../../Store/Reducers/MasterSlice";
import { useAccountTiers } from "../../../Utils/Helper/accountTierHelpers";
import {
  ExecutiveForUI,
  UnassignedAccount,
  AccountDetails,
  AccountSummaryRow,
  SecondaryExecutiveSelector,
  AssignmentNotes,
  BatchApproverForm,
  ExecutiveList,
  getDefaultBatchName,
  AIRecommendationsBanner,
  CapacityUtilizationBar,
  buildApproverSelectOptions,
} from "../SharedComponents";

type Props = {
  assignmentMode: string;
  setAssignmentMode: (v: string) => void;
  unassignedAccountsData: UnassignedAccount[];
  getModeDisplay: () => string;
  getCardButtonText: (a: UnassignedAccount) => React.ReactNode;
  getCardButtonVariant: (a: UnassignedAccount) => any;
  selectedAccountIds: string[];
  onToggleAccountSelection: (accountId: string) => void;
  onSelectAll: () => void;
  onClearAll: () => void;
  isAccountSelected: (accountId: string) => boolean;
  onProcessingAccountChange?: (
    accountName: string | null,
    accountIndex?: number
  ) => void;
  onManualModalStateChange?: (isOpen: boolean) => void;
  onBulkModalStateChange?: (isOpen: boolean) => void;
  onRefreshCounts?: () => void;
};

const UnassignedContainer: React.FC<Props> = ({
  assignmentMode,
  setAssignmentMode,
  unassignedAccountsData,
  getModeDisplay,
  getCardButtonText,
  getCardButtonVariant,
  selectedAccountIds,
  onToggleAccountSelection,
  onSelectAll,
  onClearAll,
  isAccountSelected,
  onProcessingAccountChange,
  onManualModalStateChange,
  onBulkModalStateChange,
  onRefreshCounts,
}) => {
  const { formatTier } = useAccountTiers();
  // --- new modal/step state ---
  const [showManualModal, setShowManualModal] = useState(false);
  const [manualStep, setManualStep] = useState(1); // 1 = select, 2 = batch & review & submit
  const [batchName, setBatchName] = useState("");
  const [selectedApprover, setSelectedApprover] = useState<string | null>(null);
  const [primaryExecutive, setPrimaryExecutive] = useState<string | null>(null);
  const [secondaryExecutive, setSecondaryExecutive] = useState<string | null>(
    null
  );
  const [assignmentNotes, setAssignmentNotes] = useState("");
  const [activeAccount, setActiveAccount] = useState<UnassignedAccount | null>(
    null
  );
  const [showBulkReviewModal, setShowBulkReviewModal] = useState(false);
  const [bulkStep, setBulkStep] = useState(1); // 1 = select, 2 = batch & review & submit
  const [assignMode, setAssignMode] = useState<"manual" | "auto">("manual");
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Bulk assignment state - track selections per account
  type BulkAssignmentSelection = {
    accountId: string;
    primaryExecutiveId: string | null;
    secondaryExecutiveId: string | null;
    notes?: string;
  };
  const [bulkSelections, setBulkSelections] = useState<
    Record<string, BulkAssignmentSelection>
  >({});
  const [bulkBatchName, setBulkBatchName] = useState("");
  const [bulkSelectedApprover, setBulkSelectedApprover] = useState<
    string | null
  >(null);
  const [bulkAssignMode, setBulkAssignMode] = useState<"auto" | "manual">(
    "auto"
  );
  const [bulkModalAccountIds, setBulkModalAccountIds] = useState<string[]>([]);
  // Active account tab in bulk step 1 (one tab per account)
  const [bulkActiveAccountId, setBulkActiveAccountId] = useState<string | null>(
    null
  );

  // Track pending modal open for manual mode (wait for recommendations to load)
  const [pendingModalAccount, setPendingModalAccount] =
    useState<UnassignedAccount | null>(null);
  const [pendingModalMode, setPendingModalMode] = useState<
    "auto" | "manual" | null
  >(null);

  // Track bulk submission progress
  const [isBulkSubmission, setIsBulkSubmission] = useState(false);

  // Local UI state for executives (transformed from API response)
  const [executives, setExecutives] = useState<ExecutiveForUI[]>([]);
  const [secondaryExecutives, setSecondaryExecutives] = useState<
    ExecutiveForUI[]
  >([]);

  // Default batch name when reaching step 2 (single-account): "Assignment - {account} - {today}"
  useEffect(() => {
    if (
      showManualModal &&
      manualStep === 2 &&
      activeAccount &&
      !batchName.trim()
    ) {
      setBatchName(getDefaultBatchName(activeAccount.name));
    }
  }, [showManualModal, manualStep, activeAccount?.id, activeAccount?.name]);

  // Default batch name when reaching bulk step 2
  useEffect(() => {
    if (showBulkReviewModal && bulkStep === 2 && !bulkBatchName.trim()) {
      setBulkBatchName(getDefaultBatchName("Batch Accounts"));
    }
  }, [showBulkReviewModal, bulkStep]);

  // Redux state for recommendations
  const {
    data: recommendationsData,
    loading: loadingRecommendations,
    error: recommendationsError,
  } = useAppSelector(state => state.assignments.recommendations);

  // Clear processing account name when recommendations are loaded
  // Also open modal for manual mode after recommendations finish loading
  useEffect(() => {
    if (!loadingRecommendations) {
      if (onProcessingAccountChange) {
        onProcessingAccountChange(null);
      }

      // Open modal for manual mode after recommendations are loaded (only if no error)
      if (pendingModalAccount && pendingModalMode) {
        if (!recommendationsError) {
          setActiveAccount(pendingModalAccount);
          setShowManualModal(true);
          setManualStep(1);
          setExecutives([]);
          setSecondaryExecutives([]);
          setPrimaryExecutive(null);
          setSecondaryExecutive(null);
          setAssignMode(pendingModalMode);

          // Notify parent that manual modal is now open
          if (onManualModalStateChange) {
            onManualModalStateChange(true);
          }
        }

        // Clear pending state (whether success or error)
        setPendingModalAccount(null);
        setPendingModalMode(null);
      }
    }
  }, [
    loadingRecommendations,
    pendingModalAccount,
    pendingModalMode,
    recommendationsError,
    onProcessingAccountChange,
  ]);

  // Redux state for submit assignment
  const {
    loading: submittingAssignment,
    error: submitAssignmentError,
    success: submitAssignmentSuccess,
  } = useAppSelector(state => state.assignments.submitAssignment);

  const closeShowBulkReviewModal = () => {
    setShowBulkReviewModal(false);
    setBulkStep(1);
    setBulkSelections({});
    setBulkBatchName("");
    setBulkSelectedApprover(null);
    setBulkModalAccountIds([]);
    setBulkActiveAccountId(null);
    setIsBulkSubmission(false);
    dispatch(clearRecommendations());
    dispatch(clearSubmitAssignment());
    if (onProcessingAccountChange) {
      onProcessingAccountChange(null);
    }
    // Notify parent that bulk modal is now closed
    if (onBulkModalStateChange) {
      onBulkModalStateChange(false);
    }
  };

  const openBulkReviewModal = async (mode: "auto" | "manual" = "auto") => {
    setShowBulkReviewModal(true);
    setBulkStep(1);
    setBulkBatchName("");
    setBulkSelectedApprover(null);
    setBulkAssignMode(mode);
    setBulkModalAccountIds(selectedAccountIds);
    // Notify parent that bulk modal is now open
    if (onBulkModalStateChange) {
      onBulkModalStateChange(true);
    }
  };

  // Helper: resolve primary sponsor for a customer from primary_sponsor_id + primary_sponsor array
  const getPrimarySponsorForCustomer = useCallback(
    (
      customer: { primary_sponsor_id?: string } | null,
      primarySponsorArray?: ExecutiveFromAPI[]
    ): ExecutiveFromAPI | null => {
      if (!customer?.primary_sponsor_id || !primarySponsorArray?.length)
        return null;
      return (
        primarySponsorArray.find(e => e.id === customer.primary_sponsor_id) ??
        null
      );
    },
    []
  );

  // Initialize bulk selections when recommendations data is available
  useEffect(() => {
    if (
      showBulkReviewModal &&
      recommendationsData?.bulk?.customers &&
      recommendationsData.bulk.customers.length > 0
    ) {
      const customerIds = recommendationsData.bulk.customers.map(
        c => c.customer_id
      );
      setBulkModalAccountIds(customerIds);
      // Set active tab to first account if none or current not in list
      if (!bulkActiveAccountId || !customerIds.includes(bulkActiveAccountId)) {
        setBulkActiveAccountId(
          recommendationsData.bulk.customers[0].customer_id
        );
      }

      // Check if we need to initialize - compare selected account IDs with current selections
      const recommendedAccountIds = new Set(
        recommendationsData.bulk.customers.map(c => c.customer_id)
      );
      const currentAccountIds = new Set(Object.keys(bulkSelections));

      // Initialize if selections are empty or don't match the recommendations
      if (
        currentAccountIds.size === 0 ||
        !Array.from(recommendedAccountIds).every(id =>
          currentAccountIds.has(id)
        )
      ) {
        const primarySponsorArray =
          recommendationsData.bulk.primary_sponsor ?? [];
        const initialSelections: Record<string, BulkAssignmentSelection> = {};
        recommendationsData.bulk.customers.forEach(customer => {
          const sponsor = getPrimarySponsorForCustomer(
            customer,
            Array.isArray(primarySponsorArray) ? primarySponsorArray : []
          );
          if (sponsor) {
            initialSelections[customer.customer_id] = {
              accountId: customer.customer_id,
              primaryExecutiveId: sponsor.id,
              secondaryExecutiveId: null,
              notes: bulkSelections[customer.customer_id]?.notes || "",
            };
          }
        });
        if (Object.keys(initialSelections).length > 0) {
          setBulkSelections(prev => ({ ...prev, ...initialSelections }));
        }
      }
    }
  }, [showBulkReviewModal, recommendationsData, getPrimarySponsorForCustomer]);

  // Redux hooks for admin users
  const dispatch = useAppDispatch();
  const {
    items: adminUsers,
    loading: adminUsersLoading,
    error: adminUsersError,
  } = useAppSelector(state => state.master.adminUsers);

  // Fetch admin users when component mounts or modal opens
  useEffect(() => {
    if (
      (showManualModal || showBulkReviewModal) &&
      adminUsers.length === 0 &&
      !adminUsersLoading
    ) {
      dispatch(fetchAdminUsers());
    }
  }, [
    showManualModal,
    showBulkReviewModal,
    dispatch,
    adminUsers.length,
    adminUsersLoading,
  ]);

  // Map admin users to approver options: "Name — email", grouped by role
  const { ungroupedOptions, groups: approverGroups } = useMemo(
    () => buildApproverSelectOptions(adminUsers),
    [adminUsers]
  );
  const approverOptions = ungroupedOptions;

  // Map API executive to UI executive format
  const mapExecutiveToUI = (exec: ExecutiveFromAPI): ExecutiveForUI => {
    const utilizationPercent = exec.utilization || 0;
    const availability = exec.availabilityStatus || "Unknown";

    return {
      id: exec.id,
      name: exec.name,
      title: `${exec.title} • ${exec.region || "Unknown"}`,
      availability: availability,
      utilization: utilizationPercent,
      region: exec.region || "Unknown",
      match: exec.matchScore || 0,
      warnings: exec.warnings || [],
      recommendations: exec.recommendations || [],
      accountLimit: exec.accountLimit ?? 0,
      currentAccounts: exec.current_accounts ?? 0,
    };
  };

  // Transform API response to UI format when recommendations data changes
  useEffect(() => {
    if (recommendationsData?.bulk) {
      const response = recommendationsData;
      const mode = response.mode || assignMode;
      const primarySponsorArray = response.bulk.primary_sponsor ?? [];

      // Collect all unique executives: primary_sponsor array + secondary_sponsors
      const allExecutivesMap = new Map<string, ExecutiveFromAPI>();
      if (
        Array.isArray(primarySponsorArray) &&
        primarySponsorArray.length > 0
      ) {
        primarySponsorArray.forEach((s: ExecutiveFromAPI) =>
          allExecutivesMap.set(s.id, s)
        );
      }
      if (response.bulk.secondary_sponsors?.length) {
        response.bulk.secondary_sponsors.forEach((s: ExecutiveFromAPI) => {
          if (!allExecutivesMap.has(s.id)) allExecutivesMap.set(s.id, s);
        });
      }

      const allExecutives = Array.from(allExecutivesMap.values()).map(
        mapExecutiveToUI
      );

      // Auto mode: use primary_sponsor array; manual: use all executives
      if (mode === "auto") {
        const arr = Array.isArray(primarySponsorArray)
          ? primarySponsorArray
          : [];
        setExecutives(arr.map(mapExecutiveToUI));
      } else {
        setExecutives(allExecutives);
      }

      // Secondary sponsors
      if (response.bulk.secondary_sponsors?.length) {
        setSecondaryExecutives(
          response.bulk.secondary_sponsors.map(mapExecutiveToUI)
        );
      } else {
        const primaryIds = new Set(
          Array.isArray(primarySponsorArray)
            ? primarySponsorArray.map((p: ExecutiveFromAPI) => p.id)
            : []
        );
        setSecondaryExecutives(
          allExecutives.filter(e => !primaryIds.has(e.id))
        );
      }

      // Auto-select AI recommended executive in auto mode
      if (
        mode === "auto" &&
        response.bulk.customers?.length &&
        !primaryExecutive
      ) {
        const first = response.bulk.customers[0];
        const sponsor =
          first.primary_sponsor_id &&
          Array.isArray(primarySponsorArray) &&
          primarySponsorArray.find(
            (e: ExecutiveFromAPI) => e.id === first.primary_sponsor_id
          );
        if (sponsor) setPrimaryExecutive(sponsor.id);
      }
    } else {
      setExecutives([]);
      setSecondaryExecutives([]);
    }
  }, [recommendationsData, assignMode, primaryExecutive]);

  // Fetch recommendations using Redux action
  const fetchRecommendations = async (
    accountIds: string[],
    mode: "auto" | "manual"
  ) => {
    if (accountIds.length === 0) {
      return;
    }

    // Set processing account name and index
    if (onProcessingAccountChange) {
      if (accountIds.length === 1) {
        // Single mode
        const account = unassignedAccountsData.find(
          acc => acc.id === accountIds[0]
        );
        onProcessingAccountChange(account?.name || null, 0);
      } else if (accountIds.length > 1) {
        // Bulk mode: start with first account
        const firstAccount = unassignedAccountsData.find(
          acc => acc.id === accountIds[0]
        );
        onProcessingAccountChange(firstAccount?.name || null, 0);
      }
    }

    await dispatch(
      fetchRecommendationsAction({
        accountIds,
        mode,
      })
    );

    // Clear processing account name when done
    if (onProcessingAccountChange) {
      // The loading state will be cleared by Redux, so we'll clear the name when loading is false
    }
  };

  const openManualModal = async (
    account?: UnassignedAccount | null,
    modeHint?: "auto" | "manual"
  ) => {
    const mode = modeHint || assignMode;
    if (modeHint) {
      setAssignMode(modeHint);
      setAssignmentMode(modeHint === "auto" ? "auto" : "manual");
    }

    // For manual mode, fetch recommendations first, then open modal after loading completes
    if (account) {
      // Set pending modal state to open modal after recommendations load
      setPendingModalAccount(account);
      setPendingModalMode(mode);

      // Fetch recommendations (this will trigger the loader)
      await fetchRecommendations([account.id], mode);
    } else {
      // If no account, open modal immediately (shouldn't happen in normal flow)
      setActiveAccount(null);
      setShowManualModal(true);
      setManualStep(1);
      setExecutives([]);
      setSecondaryExecutives([]);
      setPrimaryExecutive(null);
      setSecondaryExecutive(null);
    }
  };

  const closeManualModal = () => {
    setShowManualModal(false);
    setManualStep(1);
    setBatchName("");
    setSelectedApprover(null);
    setPrimaryExecutive(null);
    setSecondaryExecutive(null);
    setAssignmentNotes("");
    setActiveAccount(null);
    setExecutives([]);
    setSecondaryExecutives([]);
    setPendingModalAccount(null);
    setPendingModalMode(null);
    dispatch(clearRecommendations());
    dispatch(clearSubmitAssignment());
    if (onProcessingAccountChange) {
      onProcessingAccountChange(null);
    }
    // Notify parent that manual modal is now closed
    if (onManualModalStateChange) {
      onManualModalStateChange(false);
    }
  };

  // Handle assignment submission
  const handleSubmitAssignment = async () => {
    if (
      !activeAccount ||
      !primaryExecutive ||
      !batchName ||
      !selectedApprover
    ) {
      return;
    }

    const secondaryId =
      secondaryExecutive && secondaryExecutive !== primaryExecutive
        ? secondaryExecutive
        : null;

    const payload = {
      accountId: activeAccount.id,
      accountName: activeAccount.name,
      primaryExecutiveId: primaryExecutive,
      secondaryExecutiveIds: secondaryId ? [secondaryId] : [],
      notes: assignmentNotes || undefined,
      assignmentMode: assignMode,
      batchName: batchName,
      approverId: selectedApprover,
    };

    await dispatch(submitAssignment(payload));
  };

  // Handle bulk assignment submission - call API multiple times (once per account)
  const handleSubmitBulkAssignment = async () => {
    if (!bulkBatchName || !bulkSelectedApprover) {
      return;
    }

    // Validate all selections have primary executive
    const selectionsArray = Object.values(bulkSelections);
    const validSelections = selectionsArray.filter(
      sel => sel.primaryExecutiveId
    );

    if (validSelections.length === 0) {
      return;
    }

    // Set bulk submission state
    setIsBulkSubmission(true);

    // Submit each account assignment individually
    // Continue submitting even if one fails, but log errors
    const results = [];
    let completedCount = 0;

    for (const sel of validSelections) {
      const account = unassignedAccountsData.find(
        acc => acc.id === sel.accountId
      );

      if (!account) continue;

      const payload = {
        accountId: sel.accountId,
        accountName: account.name,
        primaryExecutiveId: sel.primaryExecutiveId!,
        secondaryExecutiveIds: sel.secondaryExecutiveId
          ? [sel.secondaryExecutiveId]
          : [],
        notes: sel.notes || undefined,
        assignmentMode: bulkAssignMode,
        batchName: bulkBatchName,
        approverId: bulkSelectedApprover,
      };

      try {
        await dispatch(submitAssignment(payload));
        results.push({ accountId: sel.accountId, success: true });
        completedCount++;
        // Clear success state after each call to prevent useEffect from firing
        dispatch(clearSubmitAssignment());
      } catch (error) {
        results.push({ accountId: sel.accountId, success: false, error });
        completedCount++;
        // Clear any error state
        dispatch(clearSubmitAssignment());
        // Continue with next assignment even if one fails
      }
    }

    // After all submissions complete, handle success
    if (completedCount === validSelections.length) {
      // Clear bulk submission state
      setIsBulkSubmission(false);

      // Handle success (only once after all submissions)
      setShowSuccessNotification(true);
      closeShowBulkReviewModal();
      // Refresh all counts so metric cards update
      if (onRefreshCounts) onRefreshCounts();
      else {
        dispatch(fetchUnassignedCustomers());
        dispatch(fetchPendingApprovals());
      }
      // Clear success state after showing notification
      setTimeout(() => {
        dispatch(clearSubmitAssignment());
      }, 100);
    }
  };

  // Handle success after submission (only for manual mode single account submissions)
  // Auto mode submissions (bulk) are handled in handleSubmitBulkAssignment
  useEffect(() => {
    if (submitAssignmentSuccess && !isBulkSubmission) {
      setShowSuccessNotification(true);
      closeManualModal();
      // Refresh all counts so metric cards update
      if (onRefreshCounts) onRefreshCounts();
      else {
        dispatch(fetchUnassignedCustomers());
        dispatch(fetchPendingApprovals());
      }
      // Clear success state after showing notification
      setTimeout(() => {
        dispatch(clearSubmitAssignment());
      }, 100);
    }
  }, [submitAssignmentSuccess, isBulkSubmission, dispatch, onRefreshCounts]);

  const goNext = () => setManualStep(s => Math.min(2, s + 1));
  const goBack = () => setManualStep(s => Math.max(1, s - 1));

  const goBulkNext = () => setBulkStep(s => Math.min(2, s + 1));
  const goBulkBack = () => setBulkStep(s => Math.max(1, s - 1));

  const handleBulkModeSwitch = async (mode: "auto" | "manual") => {
    if (mode === bulkAssignMode) return;
    setBulkAssignMode(mode);
    setBulkSelections({});
    const targetAccountIds =
      recommendationsData?.bulk?.customers?.map(c => c.customer_id) ??
      bulkModalAccountIds;
    if (targetAccountIds.length > 0) {
      await fetchRecommendations(targetAccountIds, mode);
    }
  };

  // Helper to update bulk selection for an account
  const updateBulkSelection = (
    accountId: string,
    updates: Partial<BulkAssignmentSelection>
  ) => {
    setBulkSelections(prev => ({
      ...prev,
      [accountId]: {
        ...prev[accountId],
        accountId,
        ...updates,
      },
    }));
  };

  const assessmentMode = [
    { id: "auto", label: "Auto Assign", icon: <Zap size={18} /> },
    { id: "manual", label: "Manual Assign", icon: <Pen size={18} /> },
  ];

  const COLOR_LEGEND_ITEMS = [
    {
      label: "Selected",
      subtext: "Current choice",
      border: "border border-purple-500",
    },
    {
      label: "High Match",
      subtext: "80%+ score",
      border: "border border-green-300",
    },
    {
      label: "Medium Match",
      subtext: "60-79% score",
      border: "border border-yellow-300",
    },
    {
      label: "Low Match",
      subtext: "<60% score",
      border: "border border-gray-300",
    },
  ] as const;

  return (
    <div className="bg-white rounded-lg border p-3">
      <div className=" bg-white mb-4">
        <div className="flex justify-between items-center gap-3 mb-3">
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Assignment Mode
            </h3>
            <p className="text-sm text-gray-600 m-0">
              Choose how you want to assign unassigned accounts.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex gap-3">
              <TabsComponent
                activeTab={assignmentMode}
                onTabChange={setAssignmentMode}
                variant="tag-rounded"
                tabs={assessmentMode}
              />
            </div>
            {(assignmentMode === "auto" || assignmentMode === "manual") &&
              selectedAccountIds.length > 0 && (
                <Button
                  variant="primary"
                  onClick={async () => {
                    if (selectedAccountIds.length === 1) {
                      const account = unassignedAccountsData.find(
                        acc => acc.id === selectedAccountIds[0]
                      );
                      if (account) {
                        await openManualModal(
                          account,
                          assignmentMode as "auto" | "manual"
                        );
                      }
                    } else {
                      await fetchRecommendations(
                        selectedAccountIds,
                        assignmentMode as "auto" | "manual"
                      );
                      await openBulkReviewModal(
                        assignmentMode as "auto" | "manual"
                      );
                    }
                  }}
                  disabled={loadingRecommendations}
                >
                  {loadingRecommendations
                    ? "Loading..."
                    : assignmentMode === "auto"
                      ? `Get Recommendations (${selectedAccountIds.length} ${selectedAccountIds.length === 1 ? "account" : "accounts"})`
                      : `Assign (${selectedAccountIds.length} ${selectedAccountIds.length === 1 ? "account" : "accounts"})`}
                </Button>
              )}
          </div>
        </div>

        <div className={` ${(getModeDisplay() || "").toLowerCase()}-mode`}>
          {assignmentMode === "auto" && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start">
                  <Zap className="h-5 w-5 text-blue-600 mr-3 mt-0.5" />
                  <div>
                    <h4 className="text-base font-semibold text-blue-900">
                      Auto Assignment Mode
                    </h4>
                    <p className="text-sm text-blue-700 mt-1.5 leading-relaxed font-normal">
                      AI-powered recommendations for individual and multiple
                      accounts. Click on any account to get personalized
                      executive recommendations.
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-blue-600 font-medium">
                    {selectedAccountIds.length}/5 selected
                  </span>
                  <button
                    onClick={onClearAll}
                    className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                  >
                    Clear All
                  </button>
                  <button
                    onClick={onSelectAll}
                    className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                  >
                    Select All (max 5)
                  </button>
                </div>
              </div>
            </div>
          )}
          {assignmentMode === "manual" && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start">
                  <Edit2 className="h-5 w-5 text-green-600 mr-3 mt-0.5" />
                  <div>
                    <h4 className="text-base font-semibold text-green-900">
                      Manual Assignment Mode
                    </h4>
                    <p className="text-sm text-green-700 mt-1.5 leading-relaxed font-normal">
                      Get intelligent recommendations and manually select the
                      best executive for each account. Click to select accounts,
                      then assign.
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-green-600 font-medium">
                    {selectedAccountIds.length}/5 selected
                  </span>
                  <button
                    onClick={onClearAll}
                    className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                  >
                    Clear All
                  </button>
                  <button
                    onClick={onSelectAll}
                    className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                  >
                    Select All (max 5)
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-gray-200 py-4  bg-white">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-1 tracking-tight">
              Unassigned Accounts
            </h3>
            <p className="text-sm text-gray-600 m-0 leading-relaxed">
              {unassignedAccountsData.length} accounts need executive assignment
            </p>
          </div>
          <span
            className={`px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide ${
              getModeDisplay()?.toLowerCase() === "auto"
                ? "bg-purple-100 text-purple-700"
                : "bg-blue-100 text-blue-700"
            }`}
          >
            Mode: {getModeDisplay()}
          </span>
        </div>

        <div className="mb-4">
          <SearchInput
            placeholder="Search by account name"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            rounded="normal"
          />
        </div>

        {(() => {
          const filteredAccounts = useMemo(() => {
            if (!searchQuery.trim()) {
              return unassignedAccountsData;
            }
            return unassignedAccountsData.filter(account =>
              account.name.toLowerCase().includes(searchQuery.toLowerCase())
            );
          }, [unassignedAccountsData, searchQuery]);

          if (filteredAccounts.length === 0) {
            return (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <div className="text-center">
                  <p className="text-lg font-semibold text-gray-900 mb-2">
                    {searchQuery.trim() ? "No results found" : "No data found"}
                  </p>
                  <p className="text-sm text-gray-500 font-normal">
                    {searchQuery.trim()
                      ? `No accounts match "${searchQuery}"`
                      : "No unassigned accounts available."}
                  </p>
                </div>
              </div>
            );
          }

          return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredAccounts.map(account => (
                <div
                  key={account.id}
                  className={`bg-white border rounded-lg p-4 transition-all duration-200 ease-in-out flex flex-col justify-between box-border ${
                    account.status === "pending"
                      ? "bg-yellow-50 border-orange-100"
                      : ""
                  } ${isAccountSelected(account.id) ? "border-1 border-green-500 bg-blue-50" : ""}`}
                >
                  <div className="flex justify-between items-start min-h-[2.5rem] flex-shrink-0">
                    <h4 className="text-base font-semibold text-gray-900 m-0 flex-1 leading-snug">
                      {account.name}
                    </h4>
                    {account.status === "pending" && (
                      <span className="px-2 py-1 rounded-lg text-xs font-semibold inline-flex items-center gap-1 h-6 w-fit whitespace-nowrap flex-shrink-0 bg-yellow-100 text-yellow-800 border border-yellow-200">
                        <Clock size={14} />
                        Pending Approval
                      </span>
                    )}
                  </div>

                  <div className="flex-1 min-h-[5.5rem] flex flex-col justify-start">
                    <div className="flex items-center mb-2 text-sm text-gray-600 h-5 gap-2 font-normal">
                      <Building2 size={16} className="text-gray-500 shrink-0" />
                      <span>{account.industry}</span>
                    </div>
                    <div className="flex items-center mb-2 text-sm text-gray-600 h-5 gap-2 font-normal">
                      <MapPin size={16} className="text-gray-500 shrink-0" />
                      <span>{account.location}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm text-gray-600 font-normal">
                      <div className="flex items-center h-5">
                        <span>
                          {formatTier(account.tier) || account.tier || "-"}
                        </span>
                      </div>
                      <div className="flex items-center mb-0 h-5">
                        <span>{account.value}</span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-auto pt-0 h-15 flex items-center flex-shrink-0">
                    <Button
                      className={`w-full ${account.status === "pending" ? "border border-amber-200" : ""}`}
                      variant={getCardButtonVariant(account)}
                      disabled={account.status === "pending"}
                      onClick={() => {
                        if (account.status === "pending") return;

                        // Both auto and manual modes: toggle selection
                        onToggleAccountSelection(account.id);
                      }}
                    >
                      {getCardButtonText(account)}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      {/* Manual Assign Modal (multi-step) */}
      {showManualModal && (
        <Modal
          isOpen={showManualModal}
          toggle={closeManualModal}
          title={
            manualStep === 1
              ? "Assign Executive - Step 1: Select"
              : "Assign Executive - Step 2: Review & Submit"
          }
          size="5xl"
          footer={
            manualStep === 1 ? (
              <div className="flex flex-nowrap items-center justify-end gap-3">
                <Button variant="outline-primary" onClick={closeManualModal}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={goNext}
                  disabled={!primaryExecutive}
                >
                  Review & Submit
                </Button>
              </div>
            ) : (
              <div className="flex flex-nowrap items-center justify-between gap-3">
                <Button variant="outline-primary" onClick={goBack}>
                  Back to Selection
                </Button>
                <div className="flex flex-col items-end gap-2">
                  <Button
                    variant="primary"
                    onClick={handleSubmitAssignment}
                    disabled={
                      submittingAssignment ||
                      !primaryExecutive ||
                      !batchName ||
                      !selectedApprover
                    }
                  >
                    {submittingAssignment
                      ? "Submitting..."
                      : "Confirm & Submit for Approval"}
                  </Button>
                  {submitAssignmentError && (
                    <p className="text-red-600 text-sm m-0 text-right max-w-md">
                      {submitAssignmentError}
                    </p>
                  )}
                </div>
              </div>
            )
          }
        >
          <div className="px-0">
            {manualStep === 1 && (
              <>
                {/* Assignment Mode */}
                <div className="mb-4 flex items-center gap-4">
                  <h6 className="text-base font-medium text-gray-900 tracking-wide">
                    Assignment Mode
                  </h6>
                  <div className="flex flex-wrap items-center gap-4">
                    <RadioButton
                      name="assignMode"
                      value="auto"
                      label="Auto Match"
                      checked={assignMode === "auto"}
                      onChange={async () => {
                        setAssignMode("auto");
                        setAssignmentMode("auto");
                        if (activeAccount) {
                          await fetchRecommendations(
                            [activeAccount.id],
                            "auto"
                          );
                          if (executives.length > 0) {
                            const best = [...executives].sort(
                              (a, b) => b.match - a.match
                            )[0];
                            setPrimaryExecutive(best?.id);
                            if (best?.id && best.id === secondaryExecutive) {
                              setSecondaryExecutive(null);
                            }
                          }
                        }
                      }}
                    />
                    <RadioButton
                      name="assignMode"
                      value="manual"
                      label="Manual Select"
                      checked={assignMode === "manual"}
                      onChange={async () => {
                        setAssignMode("manual");
                        setAssignmentMode("manual");
                        setPrimaryExecutive(null);
                        if (activeAccount) {
                          await fetchRecommendations(
                            [activeAccount.id],
                            "manual"
                          );
                        }
                      }}
                    />
                  </div>
                </div>

                {assignMode === "auto" ? (
                  <AIRecommendationsBanner tier={activeAccount?.tier} />
                ) : (
                  <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 p-3 rounded-xl mb-4">
                    <h6 className="text-sm font-semibold text-purple-900 mb-2 uppercase tracking-wide">
                      Manual Selection
                    </h6>
                    <p className="mb-0 text-gray-700 text-sm leading-relaxed font-normal">
                      Select multiple executives to compare and choose the best
                      fit.
                    </p>
                  </div>
                )}

                {/* Color Legend */}
                <div className="mb-4 p-3 border rounded-xl">
                  <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3 m-0">
                    Color Legend
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {COLOR_LEGEND_ITEMS.map((item, index) => (
                      <div
                        key={index}
                        className={`flex items-center justify-center p-3 bg-white ${item.border} rounded-lg min-h-[4rem]`}
                      >
                        <div className="text-center">
                          <div className="font-semibold text-xs text-gray-900 mb-0.5">
                            {item.label}
                          </div>
                          <div className="text-xs text-gray-600 font-normal">
                            {item.subtext}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Account Details */}
                <div className="mb-6 p-3 rounded-xl border border-gray-200 bg-gray-50/50">
                  <h6 className="text-md font-semibold text-gray-900 mb-3 tracking-wide border-b pb-2">
                    {activeAccount?.name}
                  </h6>
                  <AccountDetails account={activeAccount} showTitle={false} />
                </div>

                <ExecutiveList
                  executives={executives}
                  selectedId={primaryExecutive}
                  onSelect={id => {
                    setPrimaryExecutive(id);
                    if (id != null && id === secondaryExecutive) {
                      setSecondaryExecutive(null);
                    }
                  }}
                  mode={assignMode}
                  loading={loadingRecommendations}
                  error={recommendationsError}
                  aiRecommendedId={
                    assignMode === "auto"
                      ? (recommendationsData?.bulk?.customers?.[0]
                          ?.primary_sponsor_id ?? null)
                      : null
                  }
                />

                {/* Secondary Executive (Optional) */}
                <SecondaryExecutiveSelector
                  executives={secondaryExecutives}
                  selectedId={secondaryExecutive}
                  onSelect={setSecondaryExecutive}
                  excludeId={primaryExecutive}
                />

                {/* Assignment Notes (Optional) */}
                <AssignmentNotes
                  value={assignmentNotes}
                  onChange={setAssignmentNotes}
                />
              </>
            )}

            {manualStep === 2 && (
              <>
                <div className="mb-4">
                  <h4 className="text-base font-semibold text-gray-900 m-0 tracking-tight">
                    Review Assignment Summary
                  </h4>
                  <p className="text-gray-500 text-sm mt-1 mb-0">
                    Confirm account and executive assignments grouped by
                    sponsor.
                  </p>
                </div>
                <div className="space-y-3 mb-4">
                  {(() => {
                    const primaryExec =
                      executives.find(m => m.id === primaryExecutive) ||
                      secondaryExecutives.find(m => m.id === primaryExecutive);
                    const secondaryExec = secondaryExecutive
                      ? executives.find(m => m.id === secondaryExecutive) ||
                        secondaryExecutives.find(
                          m => m.id === secondaryExecutive
                        )
                      : null;

                    const sponsors: Array<{
                      exec: ExecutiveForUI;
                      accounts: UnassignedAccount[];
                    }> = [];
                    if (primaryExec && activeAccount) {
                      sponsors.push({
                        exec: primaryExec,
                        accounts: [activeAccount],
                      });
                    }

                    return sponsors.map(({ exec, accounts }) => {
                      const currentAccounts = exec.currentAccounts ?? 0;
                      const accountLimit = exec.accountLimit ?? 1;

                      return (
                        <Accordion
                          key={exec.id}
                          defaultOpen={false}
                          className="border border-gray-200 shadow-sm bg-white overflow-hidden mb-0"
                          title={(() => {
                            const match = exec.match ?? 0;
                            const matchLevel =
                              match >= 80
                                ? "high"
                                : match >= 60
                                  ? "medium"
                                  : "low";
                            const matchColorClass =
                              matchLevel === "high"
                                ? "text-green-600"
                                : matchLevel === "medium"
                                  ? "text-yellow-600"
                                  : "text-gray-600";
                            return (
                              <div className="flex items-center gap-4 w-full text-left pr-1">
                                <div className="flex-shrink-0 w-11 h-11 rounded-full bg-green-500 flex items-center justify-center text-white ring-2 ring-white shadow-sm">
                                  <Users className="w-5 h-5" strokeWidth={2} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-semibold text-gray-900 truncate">
                                      {exec.name}
                                    </span>
                                    <span className="inline-flex items-center gap-x-2.5 px-2 py-0.5 rounded-md bg-gray-50 border border-gray-100">
                                      <span
                                        className={`text-xs font-medium ${matchColorClass}`}
                                      >
                                        Match: {match}%
                                      </span>
                                    </span>
                                    <span className="shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-md bg-indigo-100 text-indigo-700 uppercase tracking-wide">
                                      Primary Sponsor
                                    </span>
                                    <span className="shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-md bg-gray-100 text-gray-700">
                                      {accounts.length} account
                                      {accounts.length !== 1 ? "s" : ""}
                                    </span>
                                  </div>
                                  <div className="text-sm text-gray-600 truncate mt-1">
                                    {exec.title}
                                    {exec.region && ` • ${exec.region}`}
                                  </div>
                                </div>
                                <CapacityUtilizationBar
                                  active={currentAccounts}
                                  pending={accounts.length}
                                  total={accountLimit}
                                  compact
                                />
                              </div>
                            );
                          })()}
                        >
                          <div className="border-t border-gray-200 bg-white">
                            {accounts.map((account, idx) => (
                              <AccountSummaryRow
                                key={account.id}
                                account={account}
                                secondaryLabel={
                                  secondaryExec
                                    ? secondaryExec.name
                                    : "Not selected"
                                }
                                secondaryIsSelected={!!secondaryExec}
                                notes={assignmentNotes}
                                isLast={idx === accounts.length - 1}
                              />
                            ))}
                          </div>
                        </Accordion>
                      );
                    });
                  })()}
                </div>
                <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden mb-6">
                  <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/60">
                    <h5 className="text-base font-semibold text-gray-900 m-0 tracking-tight">
                      Batch & Approver
                    </h5>
                    <p className="text-gray-600 text-sm leading-relaxed mt-1 mb-0">
                      Enter a batch name and select an approver, then review the
                      summary below before submitting.
                    </p>
                  </div>
                  <div className="p-5">
                    <BatchApproverForm
                      batchName={batchName}
                      onBatchNameChange={setBatchName}
                      selectedApprover={selectedApprover}
                      onApproverChange={setSelectedApprover}
                      approverOptions={approverOptions}
                      approverGroups={approverGroups}
                      adminUsersLoading={adminUsersLoading}
                      adminUsersError={adminUsersError}
                    />
                  </div>
                </div>
                <div className="border border-gray-200 rounded-xl bg-amber-50/80 border-amber-200/50 px-4 py-3.5">
                  <p className="font-semibold text-amber-900 m-0 text-sm">
                    Please review carefully
                  </p>
                  <p className="text-amber-800/90 m-0 text-sm leading-relaxed mt-1">
                    This assignment will be sent to the selected approver for
                    review.
                  </p>
                </div>
              </>
            )}
          </div>
        </Modal>
      )}
      {showBulkReviewModal &&
        (() => {
          const bulkAccountIds =
            recommendationsData?.bulk?.customers?.map(c => c.customer_id) ??
            bulkModalAccountIds;
          const completedCount = bulkAccountIds.filter(
            id => !!bulkSelections[id]?.primaryExecutiveId
          ).length;
          const totalCount = bulkAccountIds.length;
          const isAllComplete = completedCount === totalCount;
          const subtitle =
            bulkStep === 1 ? (
              <span
                className={`inline-flex items-center gap-2.5 py-1.5 px-3 rounded-lg text-sm font-medium transition-all ${
                  isAllComplete
                    ? "bg-indigo-100 border border-indigo-200 text-indigo-700 shadow-sm"
                    : "bg-slate-50 border border-slate-200 text-slate-600"
                }`}
              >
                <span className="flex items-baseline gap-1">
                  <span className="font-bold text-gray-900">
                    {completedCount}
                  </span>
                  <span className="text-gray-500">/</span>
                  <span className="font-semibold text-gray-700">
                    {totalCount}
                  </span>
                  <span className="ml-1 text-gray-500">Completed</span>
                </span>
              </span>
            ) : null;
          const bulkCustomersForFooter =
            recommendationsData?.bulk?.customers ?? [];
          const bulkAccountListForFooter =
            bulkCustomersForFooter.length > 0
              ? bulkCustomersForFooter.map(c => ({
                  customer_id: c.customer_id,
                  account: unassignedAccountsData.find(
                    acc => acc.id === c.customer_id
                  ),
                  customer: c,
                }))
              : bulkModalAccountIds
                  .map(id => ({
                    customer_id: id,
                    account: unassignedAccountsData.find(acc => acc.id === id),
                    customer: null as (typeof bulkCustomersForFooter)[0] | null,
                  }))
                  .filter(item => item.account);
          const effectiveActiveIdForFooter =
            bulkActiveAccountId &&
            bulkAccountListForFooter.some(
              a => a.customer_id === bulkActiveAccountId
            )
              ? bulkActiveAccountId
              : (bulkAccountListForFooter[0]?.customer_id ?? null);
          const currentTabIndexForFooter = bulkAccountListForFooter.findIndex(
            a => a.customer_id === effectiveActiveIdForFooter
          );
          const canGoBackForFooter = currentTabIndexForFooter > 0;
          const canGoNextForFooter =
            currentTabIndexForFooter >= 0 &&
            currentTabIndexForFooter < bulkAccountListForFooter.length - 1;

          const bulkFooter =
            bulkStep === 1 ? (
              <div className="flex items-center justify-end gap-2 w-full">
                <div className="flex items-center gap-3">
                  {bulkAccountListForFooter.length > 1 && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline-primary"
                        onClick={() => {
                          if (canGoBackForFooter) {
                            const prev =
                              bulkAccountListForFooter[
                                currentTabIndexForFooter - 1
                              ];
                            if (prev) setBulkActiveAccountId(prev.customer_id);
                          }
                        }}
                        disabled={!canGoBackForFooter}
                        className="flex items-center gap-1.5"
                      >
                        <ChevronLeft size={16} strokeWidth={2} />
                        Back
                      </Button>
                      <Button
                        variant="outline-primary"
                        onClick={() => {
                          if (canGoNextForFooter) {
                            const next =
                              bulkAccountListForFooter[
                                currentTabIndexForFooter + 1
                              ];
                            if (next) setBulkActiveAccountId(next.customer_id);
                          }
                        }}
                        disabled={!canGoNextForFooter}
                        className="flex items-center gap-1.5"
                      >
                        Next
                        <ChevronRight size={16} strokeWidth={2} />
                      </Button>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    variant="primary"
                    onClick={goBulkNext}
                    disabled={
                      !(
                        bulkAccountIds.length > 0 &&
                        bulkAccountIds.every(
                          id => !!bulkSelections[id]?.primaryExecutiveId
                        )
                      )
                    }
                  >
                    Review & Submit
                  </Button>
                </div>
              </div>
            ) : bulkStep === 2 ? (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <Button variant="outline-primary" onClick={goBulkBack}>
                  Back to Selection
                </Button>
                <div className="flex flex-col items-end gap-2">
                  <Button
                    variant="primary"
                    onClick={handleSubmitBulkAssignment}
                    disabled={
                      submittingAssignment ||
                      !bulkBatchName ||
                      !bulkSelectedApprover ||
                      Object.values(bulkSelections).filter(
                        sel => sel.primaryExecutiveId
                      ).length === 0
                    }
                  >
                    {submittingAssignment
                      ? "Submitting..."
                      : "Confirm & Submit for Approval"}
                  </Button>
                  {submitAssignmentError && (
                    <p className="text-red-600 text-sm m-0 text-right max-w-md">
                      {submitAssignmentError}
                    </p>
                  )}
                </div>
              </div>
            ) : undefined;

          return (
            <Modal
              isOpen={showBulkReviewModal}
              toggle={closeShowBulkReviewModal}
              title={`Bulk Assignment - Step ${bulkStep}: ${
                bulkStep === 1 ? "Select" : "Review & Submit"
              }`}
              subtitle={subtitle}
              footer={bulkFooter}
              size="5xl"
            >
              <div className="px-0">
                {!recommendationsError && (
                  <>
                    {bulkStep === 1 && (
                      <>
                        <div>
                          <div className="mb-4 flex items-center gap-4">
                            <h6 className="text-base font-medium text-gray-900 tracking-wide">
                              Assignment Mode
                            </h6>
                            <div className="flex flex-wrap items-center gap-4">
                              <RadioButton
                                name="bulkAssignMode"
                                value="auto"
                                label="Auto Match"
                                checked={bulkAssignMode === "auto"}
                                onChange={() => handleBulkModeSwitch("auto")}
                              />
                              <RadioButton
                                name="bulkAssignMode"
                                value="manual"
                                label="Manual Select"
                                checked={bulkAssignMode === "manual"}
                                onChange={() => handleBulkModeSwitch("manual")}
                              />
                            </div>
                          </div>
                          {(() => {
                            const bulkCustomers =
                              recommendationsData?.bulk?.customers ?? [];
                            const activeId =
                              bulkActiveAccountId &&
                              bulkCustomers.some(
                                c => c.customer_id === bulkActiveAccountId
                              )
                                ? bulkActiveAccountId
                                : bulkCustomers[0]?.customer_id;
                            const activeAccount = activeId
                              ? unassignedAccountsData.find(
                                  acc => acc.id === activeId
                                )
                              : null;
                            return bulkAssignMode === "auto" ? (
                              <AIRecommendationsBanner
                                tier={activeAccount?.tier}
                                variant="bulk"
                              />
                            ) : (
                              <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 p-3 rounded-xl mb-4">
                                <h6 className="text-sm font-semibold text-purple-900 mb-2 uppercase tracking-wide">
                                  Manual Selection
                                </h6>
                                <p className="mb-0 text-gray-700 text-sm leading-relaxed font-normal">
                                  Select executives for each account. Switch
                                  between account tabs to set primary/secondary
                                  executives and notes.
                                </p>
                              </div>
                            );
                          })()}

                          {/* Color Legend */}
                          <div className="mb-4 p-3 border rounded-xl">
                            <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3 m-0">
                              Color Legend
                            </p>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                              {COLOR_LEGEND_ITEMS.map((item, index) => (
                                <div
                                  key={index}
                                  className={`flex items-center justify-center p-3 bg-white ${item.border} rounded-lg min-h-[4rem]`}
                                >
                                  <div className="text-center">
                                    <div className="font-semibold text-xs text-gray-900 mb-0.5">
                                      {item.label}
                                    </div>
                                    <div className="text-xs text-gray-600 font-normal">
                                      {item.subtext}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                        {/* Account tabs: one tab per account */}
                        {(() => {
                          const bulkCustomers =
                            recommendationsData?.bulk?.customers ?? [];
                          const bulkAccountList =
                            bulkCustomers.length > 0
                              ? bulkCustomers.map(c => ({
                                  customer_id: c.customer_id,
                                  account: unassignedAccountsData.find(
                                    acc => acc.id === c.customer_id
                                  ),
                                  customer: c,
                                }))
                              : bulkModalAccountIds
                                  .map(id => ({
                                    customer_id: id,
                                    account: unassignedAccountsData.find(
                                      acc => acc.id === id
                                    ),
                                    customer: null as
                                      | (typeof bulkCustomers)[0]
                                      | null,
                                  }))
                                  .filter(item => item.account);

                          const effectiveActiveId =
                            bulkActiveAccountId &&
                            bulkAccountList.some(
                              a => a.customer_id === bulkActiveAccountId
                            )
                              ? bulkActiveAccountId
                              : (bulkAccountList[0]?.customer_id ?? null);

                          const bulkTabs = bulkAccountList.map(item => {
                            const hasPrimarySelected =
                              !!bulkSelections[item.customer_id]
                                ?.primaryExecutiveId;
                            const name = item.account?.name ?? item.customer_id;
                            return {
                              id: item.customer_id,
                              label: (
                                <span className="inline-flex items-center gap-2.5">
                                  <span>{name}</span>
                                  <span
                                    className={`inline-flex shrink-0 items-center justify-center w-4 h-4 rounded-full transition-colors ${
                                      hasPrimarySelected
                                        ? "bg-indigo-500 text-white"
                                        : ""
                                    }`}
                                    title={
                                      hasPrimarySelected
                                        ? "Primary executive selected"
                                        : "Select primary executive"
                                    }
                                    aria-label={
                                      hasPrimarySelected
                                        ? "Primary selected"
                                        : "Primary not selected"
                                    }
                                  >
                                    {hasPrimarySelected ? (
                                      <Check
                                        size={8}
                                        strokeWidth={2.5}
                                        aria-hidden
                                      />
                                    ) : (
                                      <CircleFadingArrowUp
                                        size={16}
                                        strokeWidth={2}
                                        className="text-slate-400"
                                        aria-hidden
                                      />
                                    )}
                                  </span>
                                </span>
                              ),
                            };
                          });

                          const selectedItem = bulkAccountList.find(
                            a => a.customer_id === effectiveActiveId
                          );

                          return (
                            <div className="rounded-xl border border-gray-200 bg-white overflow-hidden mb-4">
                              {/* Tab bar and content share one card so they feel connected */}
                              <div className="border-b border-gray-200 bg-gray-50/70">
                                <div className="flex items-center px-4 pb-0 gap-2">
                                  <div className="flex-1 min-w-0 overflow-x-auto">
                                    <TabsComponent
                                      tabs={bulkTabs}
                                      activeTab={effectiveActiveId}
                                      onTabChange={id =>
                                        setBulkActiveAccountId(id)
                                      }
                                      variant="tab-underline"
                                      className="flex flex-wrap gap-0 min-w-0 border-0"
                                    />
                                  </div>
                                  <div className="relative group shrink-0 py-2 flex items-center gap-1.5">
                                    <span className="text-sm font-medium text-gray-600">
                                      Info
                                    </span>
                                    <Info
                                      size={18}
                                      className="text-gray-500 cursor-help hover:text-gray-700"
                                      aria-label="Account tabs info"
                                    />
                                    <div className="absolute right-0 top-full mt-1 w-72 p-3 text-xs font-normal text-white bg-gray-900 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity z-50 pointer-events-none whitespace-normal">
                                      Switch between account tabs to set
                                      primary/secondary executives and notes per
                                      account.
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {selectedItem && (
                                <div className="p-4 pt-5 bg-white">
                                  <div className="mb-5 p-4 rounded-lg border border-gray-200">
                                    <AccountDetails
                                      showTitle={false}
                                      account={selectedItem.account || null}
                                    />
                                  </div>

                                  {bulkAssignMode === "manual" ? (
                                    <ExecutiveList
                                      executives={(() => {
                                        const allExecutivesMap = new Map<
                                          string,
                                          ExecutiveFromAPI
                                        >();
                                        const primaryArr =
                                          recommendationsData?.bulk
                                            ?.primary_sponsor ?? [];
                                        if (Array.isArray(primaryArr)) {
                                          primaryArr.forEach(
                                            (s: ExecutiveFromAPI) =>
                                              allExecutivesMap.set(s.id, s)
                                          );
                                        }
                                        recommendationsData?.bulk?.secondary_sponsors?.forEach(
                                          (s: ExecutiveFromAPI) => {
                                            if (!allExecutivesMap.has(s.id)) {
                                              allExecutivesMap.set(s.id, s);
                                            }
                                          }
                                        );
                                        return Array.from(
                                          allExecutivesMap.values()
                                        ).map(mapExecutiveToUI);
                                      })()}
                                      selectedId={
                                        bulkSelections[selectedItem.customer_id]
                                          ?.primaryExecutiveId || null
                                      }
                                      onSelect={id =>
                                        updateBulkSelection(
                                          selectedItem.customer_id,
                                          { primaryExecutiveId: id }
                                        )
                                      }
                                      mode="manual"
                                      loading={loadingRecommendations}
                                      error={recommendationsError}
                                      getPendingCountForExecutive={execId =>
                                        Object.values(bulkSelections).filter(
                                          s => s.primaryExecutiveId === execId
                                        ).length
                                      }
                                    />
                                  ) : (
                                    <ExecutiveList
                                      executives={(() => {
                                        const primaryArr =
                                          recommendationsData?.bulk
                                            ?.primary_sponsor ?? [];
                                        return Array.isArray(primaryArr)
                                          ? primaryArr.map(mapExecutiveToUI)
                                          : [];
                                      })()}
                                      selectedId={
                                        bulkSelections[selectedItem.customer_id]
                                          ?.primaryExecutiveId || null
                                      }
                                      onSelect={id =>
                                        updateBulkSelection(
                                          selectedItem.customer_id,
                                          { primaryExecutiveId: id }
                                        )
                                      }
                                      mode="auto"
                                      loading={loadingRecommendations}
                                      error={recommendationsError}
                                      getPendingCountForExecutive={execId =>
                                        Object.values(bulkSelections).filter(
                                          s => s.primaryExecutiveId === execId
                                        ).length
                                      }
                                      aiRecommendedId={(() => {
                                        const c =
                                          selectedItem.customer ??
                                          recommendationsData?.bulk?.customers?.find(
                                            (cust: { customer_id: string }) =>
                                              cust.customer_id ===
                                              selectedItem.customer_id
                                          );
                                        return c?.primary_sponsor_id ?? null;
                                      })()}
                                    />
                                  )}

                                  <SecondaryExecutiveSelector
                                    key={`secondary-${selectedItem.customer_id}`}
                                    executives={
                                      bulkAssignMode === "manual"
                                        ? (() => {
                                            const allMap = new Map<
                                              string,
                                              ExecutiveFromAPI
                                            >();
                                            const primArr =
                                              recommendationsData?.bulk
                                                ?.primary_sponsor ?? [];
                                            if (Array.isArray(primArr)) {
                                              primArr.forEach(
                                                (s: ExecutiveFromAPI) =>
                                                  allMap.set(s.id, s)
                                              );
                                            }
                                            recommendationsData?.bulk?.secondary_sponsors?.forEach(
                                              (s: ExecutiveFromAPI) => {
                                                if (!allMap.has(s.id)) {
                                                  allMap.set(s.id, s);
                                                }
                                              }
                                            );
                                            return Array.from(
                                              allMap.values()
                                            ).map(mapExecutiveToUI);
                                          })()
                                        : recommendationsData?.bulk?.secondary_sponsors
                                            ?.filter(
                                              (sec: ExecutiveFromAPI) =>
                                                sec.id !==
                                                (bulkSelections[
                                                  selectedItem.customer_id
                                                ]?.primaryExecutiveId ||
                                                  selectedItem.customer
                                                    ?.primary_sponsor_id)
                                            )
                                            .map(mapExecutiveToUI)
                                            .filter(
                                              (exec, idx, arr) =>
                                                arr.findIndex(
                                                  e => e.id === exec.id
                                                ) === idx
                                            ) || []
                                    }
                                    selectedId={
                                      bulkSelections[selectedItem.customer_id]
                                        ?.secondaryExecutiveId || null
                                    }
                                    onSelect={id =>
                                      updateBulkSelection(
                                        selectedItem.customer_id,
                                        {
                                          secondaryExecutiveId: id,
                                        }
                                      )
                                    }
                                    excludeId={
                                      bulkSelections[selectedItem.customer_id]
                                        ?.primaryExecutiveId ||
                                      selectedItem.customer
                                        ?.primary_sponsor_id ||
                                      null
                                    }
                                  />

                                  <AssignmentNotes
                                    value={
                                      bulkSelections[selectedItem.customer_id]
                                        ?.notes || ""
                                    }
                                    onChange={value =>
                                      updateBulkSelection(
                                        selectedItem.customer_id,
                                        {
                                          notes: value,
                                        }
                                      )
                                    }
                                    name={`assignmentNotes-${selectedItem.customer_id}`}
                                  />
                                </div>
                              )}

                              {bulkAccountList.length === 0 && (
                                <div className="text-gray-600 text-sm font-normal p-4 bg-gray-50 rounded-lg border border-gray-200 m-4">
                                  No accounts or recommendations available.
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </>
                    )}

                    {bulkStep === 2 && (
                      <>
                        <div className="mb-4">
                          <h4 className="text-base font-semibold text-gray-900 m-0 tracking-tight">
                            Review Bulk Assignment Summary
                          </h4>
                          <p className="text-gray-500 text-sm mt-1 mb-0">
                            Confirm account and executive assignments grouped by
                            sponsor.
                          </p>
                        </div>
                        <div className="space-y-3 mb-4">
                          {(() => {
                            const allExecsMap = new Map<
                              string,
                              ExecutiveFromAPI
                            >();
                            const primaryArr =
                              recommendationsData?.bulk?.primary_sponsor ?? [];
                            if (Array.isArray(primaryArr)) {
                              primaryArr.forEach((s: ExecutiveFromAPI) =>
                                allExecsMap.set(s.id, s)
                              );
                            }
                            recommendationsData?.bulk?.secondary_sponsors?.forEach(
                              (s: ExecutiveFromAPI) => {
                                if (!allExecsMap.has(s.id)) {
                                  allExecsMap.set(s.id, s);
                                }
                              }
                            );

                            type SponsorAssignment = {
                              exec: ExecutiveFromAPI;
                              accounts: Array<{
                                account: UnassignedAccount;
                                notes?: string;
                                secondaryExecutiveId: string | null;
                              }>;
                            };
                            const sponsorMap = new Map<
                              string,
                              SponsorAssignment
                            >();

                            Object.values(bulkSelections)
                              .filter(sel => sel.primaryExecutiveId)
                              .forEach(sel => {
                                const account = unassignedAccountsData.find(
                                  acc => acc.id === sel.accountId
                                );
                                if (!account) return;

                                const execId = sel.primaryExecutiveId!;
                                const exec = allExecsMap.get(execId);
                                if (!exec) return;

                                const existing = sponsorMap.get(execId);
                                const entry = {
                                  account,
                                  notes: sel.notes,
                                  secondaryExecutiveId:
                                    sel.secondaryExecutiveId ?? null,
                                };
                                if (existing) {
                                  existing.accounts.push(entry);
                                } else {
                                  sponsorMap.set(execId, {
                                    exec,
                                    accounts: [entry],
                                  });
                                }
                              });

                            return Array.from(sponsorMap.values()).map(
                              ({ exec, accounts }) => {
                                const currentAccounts =
                                  exec.current_accounts ?? 0;
                                const accountLimit = exec.accountLimit ?? 1;

                                return (
                                  <Accordion
                                    key={exec.id}
                                    defaultOpen={false}
                                    className="border border-gray-200 shadow-sm bg-white overflow-hidden mb-0"
                                    title={(() => {
                                      const match =
                                        (exec as ExecutiveFromAPI).matchScore ??
                                        0;
                                      const matchLevel =
                                        match >= 80
                                          ? "high"
                                          : match >= 60
                                            ? "medium"
                                            : "low";
                                      const matchColorClass =
                                        matchLevel === "high"
                                          ? "text-green-600"
                                          : matchLevel === "medium"
                                            ? "text-yellow-600"
                                            : "text-gray-600";
                                      return (
                                        <div className="flex items-center gap-4 w-full text-left pr-1">
                                          <div className="flex-shrink-0 w-11 h-11 rounded-full bg-green-500 flex items-center justify-center text-white ring-2 ring-white shadow-sm">
                                            <Users
                                              className="w-5 h-5"
                                              strokeWidth={2}
                                            />
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                              <span className="font-semibold text-gray-900 truncate">
                                                {exec.name}
                                              </span>
                                              <span className="inline-flex items-center gap-x-2.5 px-2 py-0.5 rounded-md bg-gray-50 border border-gray-100">
                                                <span
                                                  className={`text-xs font-medium ${matchColorClass}`}
                                                >
                                                  Match: {match}%
                                                </span>
                                              </span>
                                              <span className="shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-md bg-indigo-100 text-indigo-700 uppercase tracking-wide">
                                                Primary Sponsor
                                              </span>
                                              <span className="shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-md bg-gray-100 text-gray-700">
                                                {accounts.length} account
                                                {accounts.length !== 1
                                                  ? "s"
                                                  : ""}
                                              </span>
                                            </div>
                                            <div className="text-sm text-gray-600 truncate mt-1">
                                              {exec.title}
                                              {exec.region &&
                                                ` • ${exec.region}`}
                                            </div>
                                          </div>
                                          <CapacityUtilizationBar
                                            active={currentAccounts}
                                            pending={accounts.length}
                                            total={accountLimit}
                                            compact
                                          />
                                        </div>
                                      );
                                    })()}
                                  >
                                    <div className="border-t border-gray-200 bg-white">
                                      {accounts.map(
                                        (
                                          {
                                            account,
                                            notes,
                                            secondaryExecutiveId,
                                          },
                                          idx
                                        ) => {
                                          const secondaryExec =
                                            secondaryExecutiveId
                                              ? allExecsMap.get(
                                                  secondaryExecutiveId
                                                )
                                              : null;
                                          return (
                                            <AccountSummaryRow
                                              key={account.id}
                                              account={account}
                                              secondaryLabel={
                                                secondaryExec
                                                  ? secondaryExec.name
                                                  : "Not selected"
                                              }
                                              secondaryIsSelected={
                                                !!secondaryExec
                                              }
                                              notes={notes}
                                              isLast={
                                                idx === accounts.length - 1
                                              }
                                            />
                                          );
                                        }
                                      )}
                                    </div>
                                  </Accordion>
                                );
                              }
                            );
                          })()}
                        </div>

                        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden mb-4">
                          <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/60">
                            <h5 className="text-base font-semibold text-gray-900 m-0 tracking-tight">
                              Batch & Approver
                            </h5>
                            <p className="text-gray-600 text-sm leading-relaxed mt-1.5 mb-0">
                              Enter a batch name and select an approver before
                              submitting.
                            </p>
                          </div>
                          <div className="p-5">
                            <BatchApproverForm
                              batchName={bulkBatchName}
                              onBatchNameChange={setBulkBatchName}
                              selectedApprover={bulkSelectedApprover}
                              onApproverChange={setBulkSelectedApprover}
                              approverOptions={approverOptions}
                              approverGroups={approverGroups}
                              adminUsersLoading={adminUsersLoading}
                              adminUsersError={adminUsersError}
                              batchNameName="bulkBatchName"
                              approverName="bulkApprover"
                            />
                          </div>
                        </div>

                        <div className="border border-gray-200 rounded-xl bg-amber-50/80 border-amber-200/50 px-4 py-3.5">
                          <p className="font-semibold text-amber-900 m-0 text-sm">
                            Please review carefully
                          </p>
                          <p className="text-amber-800/90 m-0 text-sm leading-relaxed mt-1">
                            This bulk assignment will be sent to the selected
                            approver for review.
                          </p>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </Modal>
          );
        })()}

      {/* Success Notification */}
      <SuccessNotification
        isOpen={showSuccessNotification}
        onClose={() => setShowSuccessNotification(false)}
        message="Assignment Submitted Successfully"
        details="The assignment has been submitted for approval. You can track its progress in the Approvals tab."
      />
    </div>
  );
};

export default UnassignedContainer;
